import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { CustomizeViewAsync, CustomizeViewAsyncItem } from '@forge/common';
import { useTranslation } from 'react-i18next';
import { IColumnConfig } from '../../Redux/activityModels';
import { DEFAULT_COLUMN_CONFIG } from '../../Redux/activitySlice';

// description column is always visible (non-removable) but can be reordered
const NON_REMOVABLE_COLUMN_ID = 'description';

export interface ActivityColumnPanelProps {
  columnConfig: IColumnConfig;
  anchorRef: RefObject<HTMLElement>;
  onApply: (config: IColumnConfig) => void;
  onCancel: () => void;
}

const COLUMN_I18N_KEYS: Record<string, string> = {
  updatedAt: 'activityLogLastUpdated',
  createdAt: 'activityLogCreatedOn',
  description: 'activityLogActivity',
  customerName: 'customer',
  siteName: 'activityLogSite',
  status: 'activityLogStatus',
  createdBy: 'activityLogCreatedBy',
  updatedBy: 'activityLogUpdatedBy',
  details: 'activityLogDetails',
};

const ActivityColumnPanel: React.FC<ActivityColumnPanelProps> = ({
  columnConfig,
  anchorRef,
  onApply,
  onCancel,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [localConfig, setLocalConfig] = useState<IColumnConfig>(columnConfig);
  const [resetKey, setResetKey] = useState(0);
  const { t } = useTranslation();

  // Sync local config when prop changes (e.g., reopened panel)
  useEffect(() => {
    setLocalConfig(columnConfig);
  }, [columnConfig]);

  // Outside click and Escape close the panel — Cancel button resets instead (handled via onCancel below)
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      const anchorEl = anchorRef?.current as HTMLElement | null;
      const target = e.target as Node;
      if (anchorEl && anchorEl.contains(target)) return;
      const path = e.composedPath ? e.composedPath() : [];
      const insidePortal = path.some(el => (el as Element)?.id === 'customizeview-popover');
      if (insidePortal) return;
      onCancel();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [anchorRef, onCancel]);

  // Cancel button (labelled "Reset") resets columns to default and keeps the panel open
  const handleReset = useCallback(() => {
    setLocalConfig(DEFAULT_COLUMN_CONFIG);
    setResetKey(k => k + 1);
  }, []);

  const handleApply = useCallback(
    (value: { id: string; visible: boolean }[], visibility: Record<string, boolean>, _orderedItems: string[]) => {
      const newConfig: IColumnConfig = { order: value.map(v => v.id), visibility };
      setLocalConfig(newConfig);
      onApply(newConfig);
    },
    [onApply],
  );

  return (
    <div ref={panelRef}>
      <CustomizeViewAsync
        key={`cv-${resetKey}`}
        container="popover"
        anchor={anchorRef}
        position="bottom-right"
        className="activity-column-panel-popover"
        i18nTitle={t('activityLogCustomizeColumns')}
        i18nCancel={t('activityLogFilterReset')}
        i18nApply={t('activityLogFilterApply')}
        i18nReorder={t('activityLogColumnsReorder')}
        i18nAddRemove={t('activityLogColumnsAddRemove')}
        i18nReorderDescription={t('activityLogColumnsReorderDescription')}
        i18nAddRemoveDescription={t('activityLogColumnsAddRemoveDescription')}
        onApply={handleApply}
        onCancel={handleReset}
      >
        {localConfig.order.map(id => (
          <CustomizeViewAsyncItem
            key={id}
            id={id}
            visible={localConfig.visibility[id] ?? true}
            movable={true}
            removable={id !== NON_REMOVABLE_COLUMN_ID}
          >
            {t(COLUMN_I18N_KEYS[id] ?? id)}
          </CustomizeViewAsyncItem>
        ))}
      </CustomizeViewAsync>
    </div>
  );
};

export default ActivityColumnPanel;
